// Chart module for "Episode Release Performance Comparison"
// Up to 5 episodes; line chart; fetches by episode_list and date_range.

const EP_COLORS = ["#5EBAB4", "#853ACF", "#D78846", "#1C7EBD", "#CF3A5F"]; // 5 distinct colors

export function createEpisodeComparisonChart(el, apiUrl) {
  if (!el) throw new Error('episode_comparison_chart: element not found');
  const endpoint = apiUrl || '/analytics/chart';
  let apexChart = null;
  let currentRange = '7d';
  let seriesOrder = []; // track order of episode IDs for stable colors

  function parseResponse(data) {
    // Returns { series: [...], categories: [...]|null, mode: 'category'|'xy' }
    const empty = { series: [], categories: null, mode: 'xy' };
    if (!data) return empty;

    // Preferred shape: { labels: [1..N], series: [{ id,name,data:[v1..vN] }, ...] }
    if (Array.isArray(data.labels) && Array.isArray(data.series)) {
      const s = data.series.map(s => ({ id: s.id, name: s.name, episode_number: s.episode_number, total: s.total, data: Array.isArray(s.data) ? s.data : [] }));
      return { series: s, categories: data.labels, mode: 'category' };
    }

    // Alternate: { series: [{ id,name,data: [[x,y], ...] }, ...] }
    if (Array.isArray(data.series) && data.series.length && Array.isArray(data.series[0]?.data)) {
      return { series: data.series, categories: null, mode: 'xy' };
    }

    // Fallback totals only; no series data
    return empty;
  }

  function episodeTotalsFromData(data) {
    // try compute totals for the table; if backend sends total, use it
    const totals = [];
    if (Array.isArray(data?.series)) {
      for (const s of data.series) {
        const total = s.total || (Array.isArray(s.data) ? s.data.reduce((acc, d) => acc + (Array.isArray(d) ? Number(d[1]||0) : Number(d)||0), 0) : 0);
        totals.push({ id: s.id, name: s.name, total, episode_number: s.episode_number });
      }
    } else if (Array.isArray(data?.download_data)) {
      for (const row of data.download_data) {
        totals.push({ id: row.episode_id || row.tracking_link_id, name: row.link_name || row.episode_name, total: row.total_downloads || 0, episode_number: row.episode_number });
      }
    }
    return totals;
  }

  async function fetchData(range, episodeIds) {
    const params = new URLSearchParams();
    // Note: service expects misspelled type (comparision)
    params.append('chart_type', 'episode_release_performance_comparison');
    if (range === 'custom' && episodeIds?.length) {
      // custom not currently exposed; reserved
      params.append('date_range', 'custom');
    } else {
      params.append('date_range', range);
    }
    if (Array.isArray(episodeIds) && episodeIds.length) {
      params.append('episode_list', episodeIds.join(','));
    }
    const url = `${endpoint}?${params.toString()}`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
    return await res.json();
  }

  async function update(range, episodeIds) {
    const data = await fetchData(range, episodeIds);
    const parsed = parseResponse(data);
    const series = parsed.series;
    // ensure stable colors by order of selected episode IDs
    if (Array.isArray(episodeIds)) seriesOrder = [...episodeIds];
    const options = {
      colors: EP_COLORS,
      series,
      chart: {
        height: 300,
        width: '100%',
        type: 'line',
        id: 'episode-comparison',
        fontFamily: 'manrope',
        zoom: { type: 'xy' }
      },
      stroke: { curve: 'smooth', width: 2 },
      dataLabels: { enabled: false },
      tooltip: { shared: true },
      xaxis: parsed.mode === 'category' ? { type: 'category', categories: parsed.categories } : { type: 'numeric' }
    };

    if (apexChart) {
      await apexChart.updateOptions({ series: options.series, xaxis: options.xaxis });
    } else {
      apexChart = new ApexCharts(el, options);
      await apexChart.render();
    }

    return { series, totals: episodeTotalsFromData(data) };
  }

  return {
    async load(range, episodeIds) { currentRange = range || currentRange; return update(currentRange, episodeIds || seriesOrder); },
    async setRange(range, episodeIds) { currentRange = range; return update(currentRange, episodeIds || seriesOrder); },
    async setEpisodes(episodeIds) { seriesOrder = episodeIds || []; return update(currentRange, seriesOrder); },
    destroy() { try { apexChart && apexChart.destroy && apexChart.destroy(); } catch (_) {} apexChart = null; }
  };
}
